// src/components/order/OrderDetails.jsx
import React from 'react';
import { formatCurrency, formatDateTime } from '../../utils/helpers';
import { ORDER_STATUS_COLORS, getSizeLabel } from '../../utils/constants';

/**
 * Order Details Component
 * 
 * Used in: OrderDetailPage
 * 
 * Features:
 * - Complete order information
 * - All order items with custom names
 * - Delivery address
 * - Payment details
 * - Order timeline
 */
const OrderDetails = ({ order }) => {
  if (!order) return null;
  
  return (
    <div className="space-y-6">
      {/* Order Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Order #{order.orderNumber}
            </h1>
            <p className="text-gray-600 mt-1">
              Placed on {formatDateTime(order.createdAt)}
            </p>
          </div>
          
          {/* Status Badge */}
          <span className={`
            inline-flex items-center px-4 py-2 rounded-full text-base font-semibold
            ${ORDER_STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800'}
          `}>
            {order.status}
          </span>
        </div>
      </div>
      
      {/* Refund Banner */}
      {order.refundStatus && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
          </svg>
          <p className="text-sm text-blue-800">
            {order.refundStatus === 'processed'
              ? 'Your refund has been processed and credited back to your original payment method.'
              : 'Your refund has been initiated and should reach your account in 5–7 business days.'}
          </p>
        </div>
      )}

      {/* Order Items */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Order Items</h2>
        
        <div className="space-y-4">
          {order.items?.map((item, index) => (
            <div 
              key={index} 
              className="flex gap-4 pb-4 border-b border-gray-200 last:border-b-0"
            >
              <img
                src={item.productImage || '/placeholder-product.png'}
                alt={item.productName}
                className="w-24 h-24 object-cover rounded-md"
              />
              
              <div className="flex-grow">
                <h3 className="font-semibold text-gray-900">
                  {item.productName}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Size: {getSizeLabel(item.size)} • Quantity: {item.quantity}
                </p>
                
                {/* ⭐ Custom Name Display */}
                {(item.customName || item.passingYear) && (
                  <div className="mt-2 inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-md px-3 py-1">
                    <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"/>
                    </svg>
                    <span className="text-sm">
                      <span className="font-medium text-blue-900">
                        {item.customName ? 'Custom Print:' : 'Passing year:'}
                      </span>{' '}
                      {item.customName && (
                        <span className="text-blue-700 font-semibold tracking-wide">
                          {item.customName}
                        </span>
                      )}
                      {item.passingYear && (
                        <span className="text-blue-700">
                          {item.customName ? ' • ' : ''}{item.passingYear}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                
                <p className="text-sm text-gray-700 mt-2">
                  {formatCurrency(item.price)} × {item.quantity}
                </p>
              </div>
              
              <div className="text-right">
                <p className="font-bold text-gray-900">
                  {formatCurrency(item.subtotal)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Delivery Address */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Delivery Address</h2>
        
        <div className="text-gray-700 space-y-1">
        <p className="font-semibold">{order.deliveryAddress?.fullName}</p>
        <p>{order.deliveryAddress?.phone}</p>
        <p className="pt-2">{order.deliveryAddress?.addressLine1}</p>
         {order.deliveryAddress?.addressLine2 && <p>{order.deliveryAddress.addressLine2}</p>}
        <p>{order.deliveryAddress?.city}, {order.deliveryAddress?.state} - {order.deliveryAddress?.pincode}</p>
         <p>{order.deliveryAddress?.country}</p>
        </div>
      </div>
      
      {/* Payment & Price Breakdown */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Payment Details</h2>
        
        <div className="space-y-3">
          {/* Payment Method */}
          <div className="flex justify-between text-gray-700">
            <span>Payment Method</span>
            <span className="font-medium">
              {order.paymentMethod === 'RAZORPAY' ? 'Online Payment' : 'Cash on Delivery'}
            </span>
          </div>
          
          {/* Payment Status */}
          <div className="flex justify-between text-gray-700">
            <span>Payment Status</span>
            <span className={`font-medium ${order.paymentCompleted ? 'text-green-600' : 'text-yellow-600'}`}>
              {order.paymentCompleted ? 'Paid' : 'Pending'}
            </span>
          </div>

          {/* Refund Status (only after a refund has been initiated) */}
          {order.refundStatus && (
            <div className="flex justify-between text-gray-700">
              <span>Refund Status</span>
              <span className={`font-medium ${order.refundStatus === 'processed' ? 'text-green-600' : 'text-blue-600'}`}>
                {order.refundStatus === 'processed' ? 'Refunded' : 'Refund Initiated'}
              </span>
            </div>
          )}

          <div className="border-t border-gray-200 my-4"></div>
          
          {/* Price Breakdown */}
          <div className="flex justify-between text-gray-700">
            <span>Subtotal</span>
            <span>{formatCurrency(order.subtotal)}</span>
          </div>
          
          <div className="flex justify-between text-gray-700">
            <span>Shipping</span>
            <span>
              {order.shippingCharge === 0 ? (
                <span className="text-green-600">FREE</span>
              ) : (
                formatCurrency(order.shippingCharge)
              )}
            </span>
          </div>
          
          {Math.abs((order.subtotal + order.shippingCharge) - order.totalAmount) < 0.5 ? (
            /* GST-inclusive (current) orders: tax already sits inside the subtotal */
            <div className="flex justify-between text-gray-500 text-sm">
              <span>Includes GST</span>
              <span>{formatCurrency(order.tax)}</span>
            </div>
          ) : (
            /* Legacy orders that added GST on top of the subtotal */
            <div className="flex justify-between text-gray-700">
              <span>GST</span>
              <span>{formatCurrency(order.tax)}</span>
            </div>
          )}

          <div className="border-t border-gray-200 my-4"></div>
          
          {/* Total */}
          <div className="flex justify-between text-lg font-bold text-gray-900">
            <span>Total Amount</span>
            <span>{formatCurrency(order.totalAmount)}</span>
          </div>
        </div>
      </div>
      
      {/* Tracking Info (if available) */}
      {order.trackingNumber && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Tracking Information</h2>
          
          <div className="space-y-2 text-gray-700">
            <div className="flex justify-between">
              <span>Courier</span>
              <span className="font-medium">{order.courierName}</span>
            </div>
            <div className="flex justify-between">
              <span>Tracking Number</span>
              <span className="font-mono font-medium">{order.trackingNumber}</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Notes */}
      {order.notes && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-2">Order Notes</h3>
          <p className="text-gray-700">{order.notes}</p>
        </div>
      )}
    </div>
  );
};

export default OrderDetails;